import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Tooltip,
  IconButton,
  CircularProgress
} from '@mui/material';
import {
  CreditCard,
  AccountBalance,
  Store,
  CheckCircle,
  ContentCopy,
  Payment as PaymentIcon
} from '@mui/icons-material';
import { isWireTransferEnabled } from '../../utils/featureFlags';
import { useTranslation } from 'react-i18next';

const PaymentDialog = ({
  // Payment state
  selectedMethod,
  dialogOpen,
  setDialogOpen,
  actionLoading,
  redirectData,
  
  // Credit card state
  creditCardForm,
  setCreditCardForm,
  wireTransferInfo,
  inPlaceToken,
  
  // Payment options
  paymentOptions,
  
  // Handlers
  handleCreditCardPayment,
  handleCardNumberChange,
  copyToClipboard,
  formatCardNumber
}) => {
  const { t } = useTranslation();

  // Render credit card dialog
  const renderCreditCardDialog = () => {
    // If we have redirect data, show the 3D Secure iframe
    if (redirectData && redirectData.formHtml) {
      return (
        <Dialog
          open={dialogOpen && selectedMethod === 'credit_card'}
          onClose={() => setDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CreditCard />
                Secure Payment - 3D Authentication
              </Box>
              {actionLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">
                    Processing...
                  </Typography>
                </Box>
              )}
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            <iframe
              title="3D Secure Payment"
              srcDoc={redirectData.formHtml}
              style={{
                width: '100%',
                height: '600px',
                border: 'none',
                display: 'block'
              }}
              sandbox="allow-scripts allow-forms allow-same-origin allow-top-navigation"
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setDialogOpen(false)}
              variant="outlined"
              color="inherit"
            >
              Cancel Payment
            </Button>
          </DialogActions>
        </Dialog>
      );
    }

    // Regular credit card form
    return (
      <Dialog open={dialogOpen && selectedMethod === 'credit_card'} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CreditCard />
            Credit Card Payment
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2} sx={{ width: '100%' }}>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label={t('payment.cardNumber', { ns: 'payment' })}
                  value={formatCardNumber(creditCardForm.cardNumber)}
                  onChange={handleCardNumberChange}
                  placeholder="1234 5678 9012 3456"
                  inputProps={{ maxLength: 19 }}
                  required
                />
              </Grid>
            
              <Grid size={12}>
                <TextField
                  fullWidth
                  label={t('payment.cardHolderName', { ns: 'payment' })}
                  value={creditCardForm.cardHolderName}
                  onChange={(e) => setCreditCardForm(prev => ({ ...prev, cardHolderName: e.target.value }))}
                  required
                />
              </Grid>
            
              <Grid size={4}>
                <FormControl fullWidth required>
                  <InputLabel>Month</InputLabel>
                  <Select
                    value={creditCardForm.expiryMonth}
                    onChange={(e) => setCreditCardForm(prev => ({ ...prev, expiryMonth: e.target.value }))}
                    label="Month"
                  >
                    {[...Array(12)].map((_, i) => (
                      <MenuItem key={i + 1} value={i + 1}>
                        {String(i + 1).padStart(2, '0')}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            
              <Grid size={4}>
                <FormControl fullWidth required>
                  <InputLabel>Year</InputLabel>
                  <Select
                    value={creditCardForm.expiryYear}
                    onChange={(e) => setCreditCardForm(prev => ({ ...prev, expiryYear: e.target.value }))}
                    label="Year"
                  >
                    {[...Array(10)].map((_, i) => {
                      const year = new Date().getFullYear() + i;
                      return (
                        <MenuItem key={year} value={year}>
                          {year}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
            
              <Grid size={4}>
                <TextField
                  fullWidth
                  label="CVV"
                  value={creditCardForm.cvv}
                  onChange={(e) => setCreditCardForm(prev => ({ ...prev, cvv: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                  placeholder="123"
                  inputProps={{ maxLength: 4 }}
                  required
                />
              </Grid>
            </Grid>
          
            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Amount to Pay:</strong> ₺{paymentOptions?.registration.amount}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Plan:</strong> {paymentOptions?.registration.selectedPlan.name}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreditCardPayment}
            variant="contained"
            disabled={actionLoading || !creditCardForm.cardNumber || !creditCardForm.cardHolderName || !creditCardForm.expiryMonth || !creditCardForm.expiryYear || !creditCardForm.cvv}
            startIcon={actionLoading ? <CircularProgress size={16} /> : <PaymentIcon />}
          >
            {actionLoading ? 'Processing...' : `Pay ₺${paymentOptions?.registration.amount}`}
          </Button>
        </DialogActions>
      </Dialog>
    ); 
  };

  // Render wire transfer dialog
  const renderWireTransferDialog = () => isWireTransferEnabled() && (
    <Dialog open={dialogOpen && selectedMethod === 'wire_transfer'} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccountBalance />
          Wire Transfer Instructions
        </Box>
      </DialogTitle>
      <DialogContent>
        {wireTransferInfo && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              Please complete the wire transfer within 3 days. Use the reference code as the transfer description.
            </Alert>
            
            <Grid container spacing={2} sx={{ width: '100%' }}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Paper sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">Bank Name</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body1">{wireTransferInfo.bankName}</Typography>
                    <Tooltip title="Copy">
                      <IconButton size="small" onClick={() => copyToClipboard(wireTransferInfo.bankName)}>
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Paper>
              </Grid>
              
              <Grid size={{ xs: 12, sm: 4 }}>
                <Paper sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">Account Holder</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body1">{wireTransferInfo.accountHolder}</Typography>
                    <Tooltip title="Copy">
                      <IconButton size="small" onClick={() => copyToClipboard(wireTransferInfo.accountHolder)}>
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Paper>
              </Grid>
              
              <Grid size={{ xs: 12, sm: 4 }}>
                <Paper sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">Account Number (IBAN)</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body1" fontFamily="monospace">{wireTransferInfo.accountNumber}</Typography>
                    <Tooltip title="Copy">
                      <IconButton size="small" onClick={() => copyToClipboard(wireTransferInfo.accountNumber)}>
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Paper>
              </Grid>
              
              <Grid size={{ xs: 12, sm: 4 }}>
                <Paper sx={{ p: 2, mb: 2, bgcolor: 'primary.50' }}>
                  <Typography variant="subtitle2" color="primary">Reference Code (IMPORTANT)</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="h6" fontFamily="monospace" color="primary">
                      {wireTransferInfo.referenceCode}
                    </Typography>
                    <Tooltip title="Copy">
                      <IconButton size="small" onClick={() => copyToClipboard(wireTransferInfo.referenceCode)}>
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Paper>
              </Grid>
              
              <Grid size={{ xs: 12, sm: 4 }}>
                <Paper sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">Amount</Typography>
                  <Typography variant="h6">₺{wireTransferInfo.amount} {wireTransferInfo.currency}</Typography>
                </Paper>
              </Grid>
            </Grid>
            
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>Instructions:</Typography>
              <List dense>
                {wireTransferInfo.instructions.map((instruction, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <CheckCircle color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={instruction} />
                  </ListItem>
                ))}
              </List>
            </Box>
            
            <Alert severity="warning" sx={{ mt: 2 }}>
              <strong>Expires:</strong> {new Date(wireTransferInfo.expiresAt).toLocaleString()}
            </Alert>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDialogOpen(false)}>Close</Button>
      </DialogActions>
    </Dialog>
  );

  // Render in-place dialog
  const renderInPlaceDialog = () => (
    <Dialog open={dialogOpen && selectedMethod === 'in_place'} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Store />
          Pay at Gym Reception
        </Box>
      </DialogTitle>
      <DialogContent>
        {inPlaceToken && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="success" sx={{ mb: 3 }}>
              Your payment token has been generated. Please visit our gym reception to complete payment.
            </Alert>
            
            <Paper sx={{ p: 3, textAlign: 'center', mb: 3, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Payment Token
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <Typography variant="h4" fontFamily="monospace" color="primary">
                  {inPlaceToken.paymentToken}
                </Typography>
                <Tooltip title="Copy Token">
                  <IconButton onClick={() => copyToClipboard(inPlaceToken.paymentToken)}>
                    <ContentCopy />
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Amount: ₺{inPlaceToken.amount}
              </Typography>
            </Paper>
            
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>What to do next:</Typography>
              <List>
                <ListItem>
                  <ListItemIcon><CheckCircle color="primary" /></ListItemIcon>
                  <ListItemText primary={t('payment.instructions.visitReception', { ns: 'payment' })} />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircle color="primary" /></ListItemIcon>
                  <ListItemText primary={t('payment.instructions.showToken', { ns: 'payment' })} />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircle color="primary" /></ListItemIcon>
                  <ListItemText primary={t('payment.instructions.payAtReception', { ns: 'payment' })} />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircle color="primary" /></ListItemIcon>
                  <ListItemText primary={t('payment.instructions.activation', { ns: 'payment' })} />
                </ListItem>
              </List>
            </Box>
            
            <Alert severity="info">
              <strong>Token expires:</strong> {new Date(inPlaceToken.expiresAt).toLocaleString()}
            </Alert>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDialogOpen(false)}>Close</Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <>
      {renderCreditCardDialog()}
      {renderWireTransferDialog()}
      {renderInPlaceDialog()}
    </>
  );
};

export default PaymentDialog;